import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableHead, TableRow, Switch, Typography,
  TextField, Button, Box, Paper,
} from '@mui/material';
import { listClientVendors, toggleClientVendor } from '../utils/api';
import { useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';

interface Vendor {
  vendor: string;
  is_active: boolean;
  disabled_game_ids: number[] | null;
}

const VendorList: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editVendor, setEditVendor] = useState<string | null>(null);
  const [disabledGameIdsInput, setDisabledGameIdsInput] = useState<string>('');

  useEffect(() => {
    fetchVendors();
  }, [clientId]);

  const fetchVendors = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listClientVendors(parseInt(clientId!));
      const vendorData = response.data.data || [];
      setVendors(vendorData);
    } catch (err) {
      console.error('Failed to fetch vendors', err);
      setError('Failed to load vendors. Please try again.');
      setVendors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (vendor: string, enabled: boolean) => {
    try {
      await toggleClientVendor(parseInt(clientId!), vendor, !enabled);
      fetchVendors();
    } catch (err) {
      console.error('Failed to toggle vendor', err);
      setError('Failed to toggle vendor. Please try again.');
    }
  };

  const handleEditGames = (vendor: string, currentIds: number[] | null) => {
    setEditVendor(vendor);
    setDisabledGameIdsInput(currentIds ? currentIds.join(', ') : '');
  };

  const handleSaveGames = async (vendor: string) => {
    const gameIds = disabledGameIdsInput
      .split(',')
      .map((id) => parseInt(id.trim()))
      .filter((id) => !isNaN(id));
    try {
      await toggleClientVendor(
        parseInt(clientId!),
        vendor,
        vendors.find((v) => v.vendor === vendor)!.is_active,
        gameIds
      );
      setEditVendor(null);
      setDisabledGameIdsInput('');
      fetchVendors();
    } catch (err) {
      console.error('Failed to update disabled games', err);
      setError('Failed to update disabled games. Please try again.');
    }
  };

  if (loading) return <Typography>Loading vendors...</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar role="superadmin" />
      <Box sx={{ flexGrow: 1, p: 3, mt: 8 }}>
        <Typography variant="h4" gutterBottom>
          Vendor Management
        </Typography>
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Vendor</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell>Disabled Games</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(vendors) && vendors.length > 0 ? (
                vendors.map((vendor) => (
                  <TableRow key={vendor.vendor}>
                    <TableCell>{vendor.vendor}</TableCell>
                    <TableCell>
                      <Switch
                        checked={vendor.is_active}
                        onChange={() => handleToggle(vendor.vendor, vendor.is_active)}
                      />
                    </TableCell>
                    <TableCell>
                      {editVendor === vendor.vendor ? (
                        <TextField
                          value={disabledGameIdsInput}
                          onChange={(e) => setDisabledGameIdsInput(e.target.value)}
                          placeholder="e.g., 4, 5, 6"
                          size="small"
                        />
                      ) : (
                        vendor.disabled_game_ids && vendor.disabled_game_ids.length > 0
                          ? vendor.disabled_game_ids.join(', ')
                          : 'None'
                      )}
                    </TableCell>
                    <TableCell>
                      {editVendor === vendor.vendor ? (
                        <Box>
                          <Button
                            onClick={() => handleSaveGames(vendor.vendor)}
                            color="primary"
                            sx={{ mr: 1 }}
                          >
                            Save
                          </Button>
                          <Button onClick={() => setEditVendor(null)} color="secondary">
                            Cancel
                          </Button>
                        </Box>
                      ) : (
                        <Button
                          onClick={() => handleEditGames(vendor.vendor, vendor.disabled_game_ids)}
                        >
                          Edit Games
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No vendors found for this client.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    </Box>
  );
};

export default VendorList;